import type { ComponentProps, MouseEvent, ReactElement } from 'react';
import { ButtonGroup } from '@launchpad-ui/components';
import clsx from 'clsx';

import type { CellButton } from './CellButton';
import type { BaseContentProps } from './Content';

import {
  cellAction,
  cellContent,
  cellKind,
  cellSize,
  contentTop,
} from '../../../../components/Table/StandardizedCell/styles.css';

export type CellKind = 'regular' | 'title';
export type CellSize = 'default' | 'compact' | 'tight';

export interface CellProps<TContentProps extends BaseContentProps = BaseContentProps> {
  /**
   * The content to render inside the cell.
   * Must be a valid Content component (TextContent, NumberContent, etc.)
   */
  children: ReactElement<TContentProps>;

  /**
   * The visual kind of the cell.
   * - 'regular': Normal cell with regular font weight
   * - 'title': Title cell with bold font weight
   * @default 'regular'
   */
  kind?: CellKind;

  /**
   * The size variant of the cell.
   * - 'default': Standard padding
   * - 'compact': Reduced padding
   * - 'tight': Minimal padding
   * @default 'default'
   */
  size?: CellSize;

  /**
   * Optional className to apply custom styles
   */
  className?: string;

  /**
   * The click handler for the cell.
   */
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;

  /**
   * Buttons (CellButtons) shown after the content.
   * These are supplemental buttons that don't affect cell clickability.
   *
   * @example
   * buttons={[
   *   <CellButton key="ai" onPress={() => investigate()}>AI</CellButton>,
   *   <CellButton key="flag" onPress={() => flag()}>Flag</CellButton>,
   *   <CellButton size="small" variant="default">Open</CellButton>,
   * ]}
   */
  buttons?: ReactElement<ComponentProps<typeof CellButton>>[];
}

/**
 * A generic, standardized cell component that acts as a container for table cell content.
 *
 * @example
 * ```tsx
 * // Simple cell
 * <Cell>
 *   <TextContent text="Hello World" />
 * </Cell>
 *
 * // Clickable cell (shows pointer cursor)
 * <Cell onClick={() => viewTrace()}>
 *   <TextContent text="Click anywhere" />
 * </Cell>
 *
 * // With action buttons
 * <Cell
 *   onClick={() => viewTrace()}
 *   buttons={[
 *     <CellButton key="ai" onPress={() => investigate()}>AI</CellButton>,
 *     <CellButton key="open" size="small" variant="default">Open</CellButton>,
 *   ]}
 * >
 *   <TextContent text="Trace ID" />
 * </Cell>
 *
 * // Buttons without cell click handler
 * <Cell buttons={[<CellButton key="tag" onPress={() => alert('Tagged')}>Tag</CellButton>]}>
 *   <TextContent text="Not clickable" />
 * </Cell>
 * ```
 */
export const Cell = <TContentProps extends BaseContentProps = BaseContentProps>({
  children,
  kind = 'regular',
  size = 'default',
  className,
  onClick,
  buttons,
}: CellProps<TContentProps>) => {
  const cellFillProps = {
    ...(onClick && {
      role: 'button' as const,
      tabIndex: 0,
      onClick,
      style: { cursor: 'pointer' },
    }),
  };

  return (
    <div className={clsx(cellKind[kind], className)} data-cell-kind={kind}>
      <div className={cellSize[size]} {...cellFillProps}>
        <div className={contentTop}>
          <div className={cellContent}>{children}</div>
          {buttons && buttons.length > 0 && (
            <div className={cellAction}>
              <ButtonGroup style={{ gap: 'var(--lp-size-6)' }}>{buttons}</ButtonGroup>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
